import { useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useAppStore } from '@/store/appStore';
import {
  getStructure,
  getActionsByClient,
  getActionsByCountry,
  getDropdownConfig,
  filterCountriesByPermissions,
  filterClientsByPermissions
} from '@/services/firestoreService';
import type { Country, Client, DropdownConfig, ActionGridRow } from '@/types';
import { ActionGrid } from '@/components/grid/ActionGrid';
import { ActionGridInnovation } from '@/innovation/ActionGridInnovation';
import { InnovationProjectModel } from '@/innovation/modelsInnovation';
import {
  getInnovationProjects,
  createInnovationProject,
  updateInnovationProject,
  deleteInnovationProject
} from '@/innovation/firebaseInnovationService';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { AddCountryModal } from '@/components/grid/AddCountryModal';
import { Loader2, LogOut, Settings, BarChart3, Wallet, Clock, ListFilter, Eye, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import logo from '../../../assets/logo.png';

const ENABLE_COMMERCIAL_ACTIONS = true;
const ENABLE_INNOVATION_TEAM = false;
const ALLOW_INNOVATION_ROLE_COMMERCIAL = false;
const NO_COUNTRY_VALUE = '__innovation_no_country__';
const NO_CLIENT_VALUE = '__innovation_no_client__';
const NO_COUNTRY_LABEL = 'No Country (Internal)';
const NO_CLIENT_LABEL = 'No Client (Internal)';

interface DimensionOption {
  value: string;
  label: string;
  configKey?: string;
}

interface SummaryStats {
  totalActions: number;
  totalImpact: number;
  totalAchievedImpact: number;
  topItems: [string, number][];
  dimensionLabel: string;
}

function ActionsSummaryCard({
  stats,
  dimension,
  onDimensionChange,
  dimensionOptions,
}: {
  stats: SummaryStats;
  dimension: string;
  onDimensionChange: (value: string) => void;
  dimensionOptions: DimensionOption[];
}) {
  return (
    <div className="w-full h-full flex flex-col md:flex-row items-center justify-between gap-4 px-2">
      {/* KPI 1 & 2 Group */}
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-red-50 rounded-full">
            <BarChart3 className="w-4 h-4 text-[#eb4a4a]" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Total Actions</p>
            <p className="text-lg font-bold text-[#454240] leading-none">{stats.totalActions}</p>
          </div>
        </div>

        <div className="hidden md:block w-px h-8 bg-gray-100"></div>

        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-blue-50 rounded-full">
            <Wallet className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Est. Annual Impact</p>
            <p className="text-lg font-bold text-[#454240] leading-none">
              {Math.round(stats.totalImpact).toLocaleString('es-ES')}€
            </p>
          </div>
        </div>

        <div className="hidden md:block w-px h-8 bg-gray-100"></div>

        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-green-50 rounded-full">
            <Wallet className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Achieved Annual Impact</p>
            <p className="text-lg font-bold text-[#454240] leading-none">
              {Math.round(stats.totalAchievedImpact).toLocaleString('es-ES')}€
            </p>
          </div>
        </div>
      </div>

      <div className="hidden md:block w-px h-8 bg-gray-100"></div>

      {/* KPI 3: Dynamic Breakdown - Compacto */}
      <div className="flex-1 w-full md:w-auto flex flex-col justify-center gap-1 min-w-0">
        {/* Header y Selector Estilo "Texto" */}
        <div className="flex items-center gap-2">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">TOP BY:</span>

          <div className="relative group flex items-center">
            <select
              value={dimension}
              onChange={(e) => onDimensionChange(e.target.value)}
              className="appearance-none bg-transparent text-[10px] font-extrabold text-[#eb4a4a] pr-4 py-0 cursor-pointer hover:underline focus:outline-none uppercase tracking-wide"
            >
              {dimensionOptions.map(opt => (
                <option key={opt.value} value={opt.value} className="text-gray-700 bg-white normal-case">
                  {opt.label}
                </option>
              ))}
            </select>
            <ListFilter className="w-3 h-3 text-[#eb4a4a] absolute right-0 pointer-events-none" />
          </div>
        </div>

        {/* Lista Horizontal de Items - SCROLLABLE */}
        <div className="flex flex-row gap-2 w-full overflow-x-auto pb-1 scrollbar-hide">
          {stats.topItems.map(([name, count], idx) => (
            <div key={idx} className="flex-none flex items-center justify-between bg-gray-50 px-2 py-1 rounded border border-gray-100 whitespace-nowrap">
              <span className="text-[10px] font-medium text-gray-700 mr-2" title={name}>
                {name === 'Unspecified' ? 'Empty' : name}
              </span>
              <span className="text-[10px] font-bold text-[#eb4a4a]">{count}</span>
            </div>
          ))}

          {stats.topItems.length === 0 && (
            <div className="w-full text-center text-[10px] text-gray-400 italic">No data available</div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  const { user, signOut, isManager, isInnovation, isInnovationPlusPlus } = useAuth();
  const navigate = useNavigate();
  const MAX_INNOVATION_TOP_CHIPS = 12;

  const {
    selectedCountry,
    selectedClient,
    actions,
    loading,
    setSelectedCountry,
    setSelectedClient,
    setActions,
    setLoading,
    setError,
    showDeleted,
    setShowDeleted,
  } = useAppStore();

  const canSeeInnovationTeam = ENABLE_INNOVATION_TEAM || isManager || isInnovation || isInnovationPlusPlus;
  const canSeeCommercialActions = ENABLE_COMMERCIAL_ACTIONS && (!isInnovation || ALLOW_INNOVATION_ROLE_COMMERCIAL);
  const canSeeMitigation = canSeeCommercialActions;
  const canUseInnovationInternalOptions = canSeeInnovationTeam;

  const [countries, setCountries] = useState<Record<string, Country>>({});
  const [clients, setClients] = useState<Record<string, Client>>({});
  const [dropdownConfig, setDropdownConfig] = useState<DropdownConfig | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddCountryModalOpen, setIsAddCountryModalOpen] = useState(false);
  const [gridSection, setGridSection] = useState<'commercial' | 'innovation' | 'mitigation'>('commercial');
  const [mitigationActions, setMitigationActions] = useState<ActionGridRow[]>([]);
  const [mitigationLoading, setMitigationLoading] = useState(false);
  const [innovationProjectsData, setInnovationProjectsData] =
    useState<InnovationProjectModel[]>([]);
  const [selectedInnovationProjectId, setSelectedInnovationProjectId] = useState<string | null>(null);

  const normalizeClientName = useCallback((value?: string | null) => {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }, []);

  const resolveInnovationClientId = useCallback(
    (customerName?: string | null, countryKeyOrCode?: string | null) => {
      const normalizedCustomerKey = normalizeClientName(customerName);
      if (!normalizedCustomerKey || normalizedCustomerKey === 'no client') return undefined;

      const activeClientMatch = Object.values(clients).find(
        (client) => normalizeClientName(client.name) === normalizedCustomerKey
      );
      if (activeClientMatch?.id) return activeClientMatch.id;

      const resolvedCountryEntry = Object.entries(countries).find(([key, country]) =>
        key === countryKeyOrCode ||
        country.id === countryKeyOrCode ||
        country.isoCode === countryKeyOrCode ||
        country.name === countryKeyOrCode
      );
      const resolvedCountryClients = resolvedCountryEntry?.[1]?.clients || {};
      return Object.values(resolvedCountryClients).find(
        (client) => normalizeClientName(client.name) === normalizedCustomerKey
      )?.id;
    },
    [clients, countries, normalizeClientName]
  );

  // Estado para controlar qué dimensión estamos resumiendo
  const [summaryDimension, setSummaryDimension] = useState<string>('vcpConcept');
  const [mitigationSummaryDimension, setMitigationSummaryDimension] = useState<string>('vcpConcept');
  const [innovationSummaryDimension, setInnovationSummaryDimension] = useState<string>('status');
  const [innovationChipFilterValue, setInnovationChipFilterValue] = useState<string | null>(null);

  // Opciones disponibles para el resumen
  const dimensionOptions = [
    { value: 'vcpConcept', label: 'VCP Concept', configKey: 'vcp' },
    { value: 'status', label: 'Status', configKey: 'status' },
    { value: 'typeOfAction', label: 'Type of Action', configKey: 'typeOfAction' },
    { value: 'feasibility', label: 'Feasibility', configKey: 'feasibility' },
    { value: 'brand', label: 'Brand', configKey: 'brand' },
    { value: 'familyRep', label: 'Family Reporting', configKey: 'familyRep' },
  ];

  const innovationDimensionOptions = [
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' },
    { value: 'driver', label: 'Driver' },
    { value: 'topic', label: 'Topic' },
    { value: 'project', label: 'Project' },
    { value: 'leadTechnician', label: 'Lead Technician' },
  ];

  const innovationChipSearchEnabled =
    innovationSummaryDimension === 'topic' ||
    innovationSummaryDimension === 'project' ||
    innovationSummaryDimension === 'leadTechnician';

  useEffect(() => {
    setInnovationChipFilterValue(null);
  }, [innovationSummaryDimension, selectedCountry, selectedClient, gridSection]);


  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const confirmNavigation = () => {
    if (hasUnsavedChanges) {
      return window.confirm('You have unsaved changes. Are you sure you want to leave? Changes will be lost.');
    }
    return true;
  };

  const innovationProjects = useMemo(() => {
    let filtered = innovationProjectsData;

    if (canUseInnovationInternalOptions && selectedCountry === NO_COUNTRY_VALUE) {
      filtered = filtered.filter(
        (project) => !project.country || project.country === 'GLOBAL'
      );
    } else if (selectedCountry) {
      const countryObj = countries[selectedCountry];
      const possibleCountryValues = new Set<string>();
      if (countryObj?.isoCode) possibleCountryValues.add(countryObj.isoCode);
      possibleCountryValues.add(selectedCountry);
      if (countryObj?.name) possibleCountryValues.add(countryObj.name);

      filtered = filtered.filter(project => possibleCountryValues.has(project.country));
    }
    
    if (canUseInnovationInternalOptions && selectedClient === NO_CLIENT_VALUE) {
      filtered = filtered.filter((project) => !project.clientId);
    } else if (selectedClient && selectedClient !== 'all') {
      filtered = filtered.filter(project => project.clientId === selectedClient);
    }
    return filtered;
  }, [innovationProjectsData, selectedClient, selectedCountry, countries, canUseInnovationInternalOptions]);

  const selectedInnovationProject = useMemo(
    () =>
      selectedInnovationProjectId
        ? innovationProjectsData.find((item) => item.id === selectedInnovationProjectId) || null
        : null,
    [innovationProjectsData, selectedInnovationProjectId]
  );

  const handleAddInnovationProject = useCallback(
    async (project: Omit<InnovationProjectModel, 'clientId'>) => {
      if (!user) return;
      const now = new Date();
      const creationDate =
        project.creationDate?.trim() || now.toISOString().slice(0, 10).replace(/-/g, '');

      const isNoCountrySelected = canUseInnovationInternalOptions && selectedCountry === NO_COUNTRY_VALUE;
      const requestedCountryKey =
        typeof (project as InnovationProjectModel).country === 'string'
          ? (project as InnovationProjectModel).country?.trim()
          : '';
      const resolvedCountryKey =
        requestedCountryKey && requestedCountryKey !== 'GLOBAL'
          ? requestedCountryKey
          : selectedCountry && !isNoCountrySelected
            ? selectedCountry
            : '';
      const resolvedCountry =
        resolvedCountryKey && countries[resolvedCountryKey]
          ? countries[resolvedCountryKey]
          : undefined;
      const countryCode =
        requestedCountryKey === 'GLOBAL' || isNoCountrySelected
          ? 'GLOBAL'
          : resolvedCountry?.isoCode || resolvedCountryKey || 'GLOBAL';

      const normalizedCustomer = project.customer?.trim();
      const clientIdFromForm = resolveInnovationClientId(normalizedCustomer, resolvedCountryKey || countryCode);

      const newProjectData = {
        ...project,
        country: countryCode,
        clientId:
          selectedClient &&
          selectedClient !== 'all' &&
          (!canUseInnovationInternalOptions || selectedClient !== NO_CLIENT_VALUE)
            ? selectedClient
            : clientIdFromForm,
        creationDate,
      };

      try {
        const userEmail = user.email || user.uid;
        const id = await createInnovationProject(newProjectData as any, userEmail);
        setInnovationProjectsData((prev) => [
          { ...newProjectData, id } as InnovationProjectModel,
          ...prev,
        ]);
      } catch (error) {
        console.error('Error creating innovation project in Firebase:', error);
        setError('Error creating innovation project');
        throw error; // Lanzamos el error para que el modal sepa que falló
      }
  }, [selectedCountry, selectedClient, countries, user, setError, resolveInnovationClientId]);

  const handleSelectInnovationProject = useCallback((project: InnovationProjectModel) => {
    setSelectedInnovationProjectId(project.id || null);
  }, []);

  const handleUpdateInnovationProject = useCallback(async (project: InnovationProjectModel) => {
    if (!user || !project.id) return;
    try {
      const previousProject = innovationProjectsData.find((item) => item.id === project.id);
      const isNoCountrySelected = canUseInnovationInternalOptions && selectedCountry === NO_COUNTRY_VALUE;
      const requestedCountryKey = typeof project.country === 'string' ? project.country.trim() : '';
      const resolvedCountryEntry = Object.entries(countries).find(([key, country]) =>
        key === requestedCountryKey ||
        country.id === requestedCountryKey ||
        country.isoCode === requestedCountryKey ||
        country.name === requestedCountryKey
      );
      const resolvedCountry = resolvedCountryEntry?.[1];
      const resolvedCountryKey =
        resolvedCountryEntry?.[0] ||
        (requestedCountryKey && requestedCountryKey !== 'GLOBAL'
          ? requestedCountryKey
          : selectedCountry && !isNoCountrySelected
            ? selectedCountry
            : '');
      const countryCode =
        requestedCountryKey === 'GLOBAL' || isNoCountrySelected
          ? 'GLOBAL'
          : resolvedCountry?.isoCode || resolvedCountryKey || 'GLOBAL';

      const normalizedCustomer = project.customer?.trim();
      const clientIdFromForm = resolveInnovationClientId(normalizedCustomer, resolvedCountryKey || countryCode);

      const normalizedProject = {
        ...project,
        country: countryCode,
        clientId:
          selectedClient &&
          selectedClient !== 'all' &&
          (!canUseInnovationInternalOptions || selectedClient !== NO_CLIENT_VALUE)
            ? selectedClient
            : clientIdFromForm,
        customer: normalizedCustomer || 'No Client',
      };

      const userEmail = user.email || user.uid;
      await updateInnovationProject(normalizedProject, userEmail, previousProject);
      setInnovationProjectsData((prev) => prev.map((item) => (item.id === project.id ? normalizedProject : item)));
    } catch (error) {
      console.error('Error updating innovation project in Firebase:', error);
      setError('Error updating innovation project');
    }
  }, [user, innovationProjectsData, canUseInnovationInternalOptions, selectedCountry, selectedClient, countries, setError, resolveInnovationClientId]);

  const handleDeleteInnovationProject = useCallback(async (project: InnovationProjectModel) => {
    try {
      await deleteInnovationProject(project);
      setInnovationProjectsData((prev) => prev.filter((item) => item.id !== project.id));
      setSelectedInnovationProjectId(null);
    } catch (error) {
      console.error('Error deleting innovation project in Firebase:', error);
      setError('Error deleting innovation project');
    }
  }, [setError]);

  const handleAddInnovationUpdate = useCallback(
    async (
      project: InnovationProjectModel,
      updateData: any,
      isDelete?: boolean,
      updateIdToDelete?: string
    ) => {
      if (!user) return;
      try {
        const { saveInnovationUpdate, deleteInnovationUpdate, getInnovationProjectUpdates } = await import('@/innovation/firebaseInnovationService');
        
        if (isDelete && updateIdToDelete) {
          await deleteInnovationUpdate(project, updateIdToDelete);
        } else if (updateData) {
          const userEmail = user.email || user.uid;
          await saveInnovationUpdate(project, updateData, userEmail);
        }
        
        // Refrescamos los datos del proyecto (counts, etc)
        const updatedUpdates = await getInnovationProjectUpdates(project);
        
        setInnovationProjectsData((prev) =>
          prev.map((item) =>
            item.id === project.id
              ? { 
                  ...item, 
                  updatesCount: String(updatedUpdates.length)
                }
              : item
          )
        );
      } catch (error) {
        console.error('Error updating project updates in Firebase:', error);
        setError('Error updating project updates');
        throw error; // Propagamos el error para que la UI no cierre el modal
      }
    },
    [user, setError]
  );

  const handleCloseInnovationDetails = useCallback(() => {
    setSelectedInnovationProjectId(null);
  }, []);

  const reloadInnovationProjects = useCallback(async () => {
    if (!user || !canSeeInnovationTeam) return;
    try {
      const innovationProjects = await getInnovationProjects(user);
      setInnovationProjectsData(innovationProjects);
    } catch (error) {
      console.error('Error reloading innovation projects:', error);
      setError('Error reloading innovation projects');
    }
  }, [user, canSeeInnovationTeam, setError]);

  // Función para cargar/recargar la estructura
  const loadStructure = useCallback(async () => {
    if (!user) return;
    try {
      const structure = await getStructure();
      const allowedCountries = filterCountriesByPermissions(structure, user);
      setCountries(allowedCountries);
    } catch (error: any) {
      console.error('Error loading structure:', error);
      setError(error.message || 'Error al cargar estructura');
    }
  }, [user, setError]);

  // Carga inicial
  useEffect(() => {
    const loadInitialData = async () => {
      if (!user) return;
      try {
        setInitialLoading(true);
        await loadStructure();
        const config = await getDropdownConfig();
        setDropdownConfig(config);
        
        if (canSeeInnovationTeam) {
          await reloadInnovationProjects();
        } else {
          setInnovationProjectsData([]);
          setSelectedInnovationProjectId(null);
        }
      } catch (error: any) {
        console.error('Error loading initial data:', error);
        setError(error.message || 'Error al cargar datos iniciales');
      } finally {
        setInitialLoading(false);
      }
    };
    loadInitialData();
  }, [user, setError, loadStructure, canSeeInnovationTeam, reloadInnovationProjects]);

  useEffect(() => {
    if (canUseInnovationInternalOptions) return;
    if (selectedCountry === NO_COUNTRY_VALUE) {
      setSelectedCountry(null);
    }
    if (selectedClient === NO_CLIENT_VALUE) {
      setSelectedClient(null);
    }
    if (!canSeeInnovationTeam && gridSection === 'innovation') {
      setGridSection('commercial');
    }
  }, [gridSection, selectedClient, selectedCountry, setSelectedClient, setSelectedCountry, canSeeInnovationTeam, canUseInnovationInternalOptions]);

  useEffect(() => {
    if (!canSeeCommercialActions && gridSection === 'commercial') {
      setGridSection(canSeeInnovationTeam ? 'innovation' : 'commercial');
    }
    if (!canSeeInnovationTeam && gridSection === 'innovation') {
      setGridSection('commercial');
    }
    if (!canSeeMitigation && gridSection === 'mitigation') {
      setGridSection(canSeeCommercialActions ? 'commercial' : 'innovation');
    }
  }, [canSeeCommercialActions, canSeeInnovationTeam, canSeeMitigation, gridSection]);

  const effectiveSelectedCountry = useMemo(() => {
    if (!canUseInnovationInternalOptions && selectedCountry === NO_COUNTRY_VALUE) return null;
    return selectedCountry;
  }, [selectedCountry, canUseInnovationInternalOptions]);

  const effectiveSelectedClient = useMemo(() => {
    if (!canUseInnovationInternalOptions && selectedClient === NO_CLIENT_VALUE) return null;
    return selectedClient;
  }, [selectedClient, canUseInnovationInternalOptions]);

  useEffect(() => {
    if (!effectiveSelectedCountry || !user || effectiveSelectedCountry === NO_COUNTRY_VALUE) {
      setClients({});
      return;
    }
    const country = countries[effectiveSelectedCountry];
    if (country) {
      const countryClients = country.clients || {};
      const allowedClients = filterClientsByPermissions(countryClients, user, effectiveSelectedCountry);
      setClients(allowedClients || {});
    }
  }, [effectiveSelectedCountry, countries, user]);

  useEffect(() => {
    const loadActions = async () => {
      if (!canSeeCommercialActions) {
        setActions([]);
        return;
      }
      if (
        !effectiveSelectedCountry ||
        !effectiveSelectedClient ||
        effectiveSelectedCountry === NO_COUNTRY_VALUE ||
        effectiveSelectedClient === NO_CLIENT_VALUE
      ) {
        setActions([]);
        return;
      }
      try {
        setLoading(true);
        let actionsData;
        if (effectiveSelectedClient === 'all') {
          actionsData = await getActionsByCountry(effectiveSelectedCountry, showDeleted);
        } else {
          actionsData = await getActionsByClient(effectiveSelectedCountry, effectiveSelectedClient, showDeleted);
        }
        setActions(actionsData);
      } catch (error: any) {
        console.error('Error loading actions:', error);
        setActions([]);
      }
    };
    loadActions();
  }, [effectiveSelectedCountry, effectiveSelectedClient, setActions, setLoading, setError, showDeleted, canSeeCommercialActions]);

  useEffect(() => {
    const loadMitigationActions = async () => {
      if (!canSeeMitigation) {
        setMitigationActions([]);
        return;
      }
      if (
        !effectiveSelectedCountry ||
        !effectiveSelectedClient ||
        effectiveSelectedCountry === NO_COUNTRY_VALUE ||
        effectiveSelectedClient === NO_CLIENT_VALUE
      ) {
        setMitigationActions([]);
        return;
      }
      try {
        setMitigationLoading(true);
        const actionsData = effectiveSelectedClient === 'all'
          ? await getActionsByCountry(effectiveSelectedCountry, showDeleted, 'mitigation')
          : await getActionsByClient(effectiveSelectedCountry, effectiveSelectedClient, showDeleted, 'mitigation');
        setMitigationActions(actionsData);
      } catch (error: any) {
        console.error('Error loading mitigation actions:', error);
        setMitigationActions([]);
      } finally {
        setMitigationLoading(false);
      }
    };
    loadMitigationActions();
  }, [effectiveSelectedCountry, effectiveSelectedClient, showDeleted, canSeeMitigation]);

  // --- CÁLCULO DINÁMICO DE ESTADÍSTICAS ---
  const buildSummaryStats = useCallback((actionsList: ActionGridRow[], dimension: string): SummaryStats | null => {
    if (!actionsList || actionsList.length === 0) return null;

    const totalActions = actionsList.length;
    const totalImpact = actionsList.reduce((sum, action) => {
      const vol = action.volumeYearTn || 0;
      const gm = action.gmIIEurKg || 0;
      const cost = action.cost || 0;
      return sum + ((vol * gm * 1000) - cost);
    }, 0);

    // Prepare status map for robust checking
    const statusEntry = dropdownConfig?.status;
    let statusMap: Record<string, string> = {};
    if (statusEntry) {
      if (!Array.isArray(statusEntry) && typeof statusEntry === 'object') {
        statusMap = statusEntry;
      } else if (Array.isArray(statusEntry)) {
        statusEntry.forEach(v => { statusMap[v] = v; });
      }
    }

    const totalAchievedImpact = actionsList.reduce((sum, action) => {
      const statusVal = action.status;
      const statusLabel = statusMap[statusVal] || statusVal;

      // Check if status is CS (either by code or label/mapped value)
      // This matches the logic in ActionGrid
      if (statusVal === 'CS' || statusLabel === 'CS') {
        const vol = action.volumeYearTn || 0;
        const gm = action.gmIIEurKg || 0;
        const cost = action.cost || 0;
        return sum + ((vol * gm * 1000) - cost);
      }
      return sum;
    }, 0);

    const selectedOption = dimensionOptions.find(opt => opt.value === dimension);
    const configKey = selectedOption?.configKey || dimension;

    let translationMap: Record<string, string> = {};
    if (dropdownConfig && (dropdownConfig as any)[configKey]) {
      const configEntry = (dropdownConfig as any)[configKey];
      if (!Array.isArray(configEntry) && typeof configEntry === 'object') {
        translationMap = configEntry;
      } else if (Array.isArray(configEntry)) {
        configEntry.forEach(v => { translationMap[v] = v; });
      }
    }

    const counts: Record<string, number> = {};
    actionsList.forEach(action => {
      const rawValue = (action as any)[dimension];
      const key = rawValue || 'Unspecified';
      const label = translationMap[key] || key;
      counts[label] = (counts[label] || 0) + 1;
    });

    // Remove slice to show all items
    const topItems = Object.entries(counts)
      .sort(([, a], [, b]) => b - a);

    return {
      totalActions,
      totalImpact,
      totalAchievedImpact,
      topItems,
      dimensionLabel: selectedOption?.label || 'Items'
    };
  }, [dropdownConfig, dimensionOptions]);

  const summaryStats = useMemo(
    () => buildSummaryStats(actions, summaryDimension),
    [buildSummaryStats, actions, summaryDimension]
  );

  const mitigationSummaryStats = useMemo(
    () => buildSummaryStats(mitigationActions, mitigationSummaryDimension),
    [buildSummaryStats, mitigationActions, mitigationSummaryDimension]
  );

  const innovationSummaryStats = useMemo(() => {
    if (!innovationProjects || innovationProjects.length === 0) return null;

    const totalProjects = innovationProjects.length;
    const projectsWithFiles = innovationProjects.reduce((sum, project) => {
      const hasModernFiles = Array.isArray(project.files) && project.files.length > 0;
      const hasLegacyFile = Boolean(project.fileUrl || project.fileName);
      return sum + (hasModernFiles || hasLegacyFile ? 1 : 0);
    }, 0);
    const totalUpdates = innovationProjects.reduce((sum, project) => {
      const raw = project.updatesCount;
      const parsed = typeof raw === 'number' ? raw : Number(raw || 0);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);

    const selectedOption =
      innovationDimensionOptions.find((opt) => opt.value === innovationSummaryDimension) ||
      innovationDimensionOptions[0];
    const counts: Record<string, number> = {};

    innovationProjects.forEach((project) => {
      const rawValue = (project as any)[innovationSummaryDimension];
      const key = rawValue ? String(rawValue) : 'Unspecified';
      counts[key] = (counts[key] || 0) + 1;
    });

    const topItems = Object.entries(counts).sort(([, a], [, b]) => b - a);

    return {
      totalProjects,
      projectsWithFiles,
      totalUpdates,
      topItems,
      dimensionLabel: selectedOption?.label || 'Items',
    };
  }, [innovationProjects, innovationSummaryDimension]);

  const innovationChipSearchOptions = useMemo(() => {
    if (!innovationSummaryStats) return [];
    const options = innovationSummaryStats.topItems.map(([name, count]) => ({
      value: name,
      label: name === 'Unspecified' ? 'Empty' : name,
      rightLabel: String(count),
    }));
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [innovationSummaryStats]);

  const innovationChipItemsToRender = useMemo(() => {
    if (!innovationSummaryStats) return [];
    const topItems = innovationSummaryStats.topItems.slice(0, MAX_INNOVATION_TOP_CHIPS);
    if (
      innovationChipFilterValue &&
      !topItems.some(([name]) => name === innovationChipFilterValue)
    ) {
      const selected = innovationSummaryStats.topItems.find(
        ([name]) => name === innovationChipFilterValue
      );
      return selected ? [selected, ...topItems] : topItems;
    }
    return topItems;
  }, [innovationChipFilterValue, innovationSummaryStats]);

  const filteredInnovationProjectsForGrid = useMemo(() => {
    if (gridSection !== 'innovation' || !innovationChipFilterValue) return innovationProjects;
    return innovationProjects.filter((project) => {
      const rawValue = (project as any)[innovationSummaryDimension];
      const key = rawValue ? String(rawValue) : 'Unspecified';
      return key === innovationChipFilterValue;
    });
  }, [gridSection, innovationChipFilterValue, innovationProjects, innovationSummaryDimension]);

  const handleCountryChange = (value: string) => {
    if (!confirmNavigation()) return;
    setSelectedCountry(value || null);
  };

  const handleClientChange = (value: string) => {
    if (!confirmNavigation()) return;
    setSelectedClient(value || null);
  };

  const handleDataChange = () => {
    if (!canSeeCommercialActions) return;
    if (
      effectiveSelectedCountry &&
      effectiveSelectedClient &&
      effectiveSelectedCountry !== NO_COUNTRY_VALUE &&
      effectiveSelectedClient !== NO_CLIENT_VALUE
    ) {
      const promise = effectiveSelectedClient === 'all'
        ? getActionsByCountry(effectiveSelectedCountry, showDeleted)
        : getActionsByClient(effectiveSelectedCountry, effectiveSelectedClient, showDeleted);

      promise
        .then(setActions)
        .catch((error) => {
          console.error('Error reloading actions:', error);
          setError('Error al recargar acciones');
        });
    }
  };

  const handleMitigationDataChange = () => {
    if (!canSeeMitigation) return;
    if (
      effectiveSelectedCountry &&
      effectiveSelectedClient &&
      effectiveSelectedCountry !== NO_COUNTRY_VALUE &&
      effectiveSelectedClient !== NO_CLIENT_VALUE
    ) {
      const promise = effectiveSelectedClient === 'all'
        ? getActionsByCountry(effectiveSelectedCountry, showDeleted, 'mitigation')
        : getActionsByClient(effectiveSelectedCountry, effectiveSelectedClient, showDeleted, 'mitigation');

      promise
        .then(setMitigationActions)
        .catch((error) => {
          console.error('Error reloading mitigation actions:', error);
          setError('Error al recargar acciones de mitigation');
        });
    }
  };

  // Manejador para cuando se crea un cliente desde el grid
  const handleClientCreated = async (client: { id: string, name: string }) => {
    // 1. Recargar estructura para que aparezca el nuevo cliente en el dropdown
    await loadStructure();
    // 2. Seleccionar automáticamente el nuevo cliente
    setSelectedClient(client.id);
  };

  const hasValidCommercialSelection = Boolean(
    canSeeCommercialActions &&
    effectiveSelectedCountry &&
    effectiveSelectedClient &&
    effectiveSelectedCountry !== NO_COUNTRY_VALUE &&
    effectiveSelectedClient !== NO_CLIENT_VALUE
  );
  const hasValidMitigationSelection = Boolean(
    canSeeMitigation &&
    effectiveSelectedCountry &&
    effectiveSelectedClient &&
    effectiveSelectedCountry !== NO_COUNTRY_VALUE &&
    effectiveSelectedClient !== NO_CLIENT_VALUE
  );
  const isInnovationInternalSelection =
    canUseInnovationInternalOptions &&
    (selectedCountry === NO_COUNTRY_VALUE || selectedClient === NO_CLIENT_VALUE);

  const canRenderInnovationGrid = canSeeInnovationTeam && Boolean(selectedCountry && selectedClient && dropdownConfig);
  const selectedCountryValue = effectiveSelectedCountry ?? '';
  const selectedClientValue = effectiveSelectedClient ?? '';
  const selectedCountryName =
    canUseInnovationInternalOptions && selectedCountry === NO_COUNTRY_VALUE
      ? NO_COUNTRY_LABEL
      : selectedCountry
        ? countries[selectedCountry]?.name || selectedCountry
        : '';

  useEffect(() => {
    if (!canUseInnovationInternalOptions) return;
    if (selectedCountry === NO_COUNTRY_VALUE && selectedClient !== NO_CLIENT_VALUE) {
      setSelectedClient(NO_CLIENT_VALUE);
    }
  }, [selectedCountry, selectedClient, setSelectedClient, canUseInnovationInternalOptions]);

  useEffect(() => {
    if (canSeeInnovationTeam && isInnovationInternalSelection && gridSection !== 'innovation') {
      setGridSection('innovation');
    }
  }, [gridSection, isInnovationInternalSelection, canSeeInnovationTeam]);

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 text-[#eb4a4a] animate-spin" />
      </div>
    );
  }

  // Helper para obtener el título del resumen
  const getSummaryTitle = () => {
    const selectedLabel = dimensionOptions.find(opt => opt.value === summaryDimension)?.label;
    return `Summary: ${selectedLabel || 'Overview'}`;
  };
  const getMitigationSummaryTitle = () => {
    const selectedLabel = dimensionOptions.find(opt => opt.value === mitigationSummaryDimension)?.label;
    return `Mitigation Summary: ${selectedLabel || 'Overview'}`;
  };
  const getInnovationSummaryTitle = () => {
    const selectedLabel = innovationDimensionOptions.find(opt => opt.value === innovationSummaryDimension)?.label;
    return `Innovation Summary: ${selectedLabel || 'Overview'}`;
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <header className="bg-[#eb4a4a] shadow-md z-20">
        <div className="px-6 py-3"> {/* Padding del header superior */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <img
                src={logo}
                alt="Altadia Logo"
                className="h-16 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight leading-none mb-1">
                  Commercial Action Tracker
                </h1>
                <p className="text-sm text-white/90 font-medium">
                  Welcome, {user?.displayName || user?.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {isManager && (
                <button
                  onClick={() => setShowDeleted(!showDeleted)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors shadow-sm ${showDeleted ? 'bg-[#eb4a4a] text-white' : 'bg-white text-[#eb4a4a] hover:bg-gray-100'}`}
                >
                  <Eye className="w-4 h-4" />
                  <span>{showDeleted ? 'Hide Deleted' : 'Show Deleted'}</span>
                </button>
              )}

              {isManager && (
                <>
                  <button
                    onClick={() => setIsAddCountryModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#eb4a4a] bg-white rounded-md hover:bg-gray-100 transition-colors shadow-sm"
                  >
                    <Globe className="w-4 h-4" />
                    <span>Add Country</span>
                  </button>
                  <button
                    onClick={() => { if (confirmNavigation()) navigate('/admin'); }}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#eb4a4a] bg-white rounded-md hover:bg-gray-100 transition-colors shadow-sm"
                  >
                    <Settings className="w-4 h-4" />
                    <span>Admin Panel</span>
                  </button>
                </>
              )}

              <button
                onClick={() => { if (confirmNavigation()) signOut(); }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#eb4a4a] bg-white rounded-md hover:bg-gray-100 transition-colors shadow-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>

            <AddCountryModal
              isOpen={isAddCountryModalOpen}
              onClose={() => setIsAddCountryModalOpen(false)}
              onCountryAdded={() => {
                loadStructure();
                // Optionally select the new country here if we track it, 
                // but loadStructure updates the list so user can see it.
              }}
            />

          </div>
        </div>

        {/* Filters & Summary Bar - Ajuste de Margen */}
        <div className="px-6 py-4 bg-[#f0f0f0] border-b border-[#e5e7eb] shadow-inner">
          {/* items-stretch fuerza a que ambas columnas (izq y der) tengan la misma altura */}
          <div className="flex flex-col lg:flex-row gap-4 items-stretch">

            {/* IZQUIERDA: Selectores */}
            <div className="w-full lg:w-1/4 flex flex-col gap-2">
              <div>
                <label className="block text-[10px] font-bold text-[#454240] uppercase tracking-wide mb-0.5 ml-1">
                  Country
                </label>
                <SearchableSelect
                  options={[
                    ...Object.entries(countries)
                      .map(([id, country]) => ({
                        value: id,
                        label: country.name,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                    ...(canUseInnovationInternalOptions
                      ? [{ value: NO_COUNTRY_VALUE, label: NO_COUNTRY_LABEL }]
                      : []),
                  ]}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  placeholder="Select a country..."
                  searchPlaceholder="Search country..."
                  className="shadow-sm w-full text-sm"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-[#454240] uppercase tracking-wide mb-0.5 ml-1">
                  Client
                </label>
                <SearchableSelect
                  options={[
                    ...(selectedCountry && selectedCountry !== NO_COUNTRY_VALUE && Object.keys(clients).length > 0
                      ? [{ value: 'all', label: 'All Clients' }]
                      : []),
                    ...Object.entries(clients)
                      .map(([id, client]) => ({
                        value: id,
                        label: client.name,
                      }))
                      .sort((a, b) => a.label.localeCompare(b.label)),
                    ...(canUseInnovationInternalOptions
                      ? [{ value: NO_CLIENT_VALUE, label: NO_CLIENT_LABEL }]
                      : []),
                  ]}
                  value={selectedClient}
                  onChange={handleClientChange}
                  placeholder="Select a client..."
                  searchPlaceholder="Search client..."
                  disabled={!selectedCountry}
                  className="shadow-sm w-full text-sm"
                />
              </div>
            </div>

            {/* DERECHA: Resumen Visual Compacto (Alineado Simétricamente) */}
            {/* Quitamos gap-2 del contenedor flex para controlar el espacio manualmente y que coincida con la izquierda */}
            <div className="flex-1 flex flex-col min-w-0">
              <label className="block text-[10px] font-bold text-[#454240] uppercase tracking-wide mb-0.5 ml-1 truncate">
                {gridSection === 'innovation'
                  ? getInnovationSummaryTitle()
                  : gridSection === 'mitigation'
                    ? getMitigationSummaryTitle()
                    : getSummaryTitle()}
              </label>

              {/* flex-1 hará que el card llene todo el espacio vertical restante, alineándose con el final de la columna izquierda */}
              <div
                className={`flex-1 bg-white rounded-lg border border-gray-200 shadow-sm px-4 py-2 relative flex items-center ${
                  gridSection === 'innovation' ? 'overflow-visible' : 'overflow-hidden'
                }`}
              >
                {gridSection === 'mitigation' ? (
                  mitigationSummaryStats ? (
                    <ActionsSummaryCard
                      stats={mitigationSummaryStats}
                      dimension={mitigationSummaryDimension}
                      onDimensionChange={setMitigationSummaryDimension}
                      dimensionOptions={dimensionOptions}
                    />
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 gap-1">
                      <BarChart3 className="w-5 h-5 opacity-20" />
                      <p className="text-xs font-medium">Select Country & Client to see summary</p>
                    </div>
                  )
                ) : gridSection === 'innovation' ? (
                  innovationSummaryStats ? (
                    <div className="w-full h-full flex flex-col md:flex-row items-center justify-between gap-4 px-2">
                      <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-red-50 rounded-full">
                            <BarChart3 className="w-4 h-4 text-[#eb4a4a]" />
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Total Projects</p>
                            <p className="text-lg font-bold text-[#454240] leading-none">{innovationSummaryStats.totalProjects}</p>
                          </div>
                        </div>

                        <div className="hidden md:block w-px h-8 bg-gray-100"></div>

                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-blue-50 rounded-full">
                            <Eye className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Projects With Files</p>
                            <p className="text-lg font-bold text-[#454240] leading-none">{innovationSummaryStats.projectsWithFiles}</p>
                          </div>
                        </div>

                        <div className="hidden md:block w-px h-8 bg-gray-100"></div>

                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-green-50 rounded-full">
                            <Clock className="w-4 h-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none mb-0.5">Total Updates</p>
                            <p className="text-lg font-bold text-[#454240] leading-none">{innovationSummaryStats.totalUpdates}</p>
                          </div>
                        </div>
                      </div>

                      <div className="hidden md:block w-px h-8 bg-gray-100"></div>

                      <div className="flex-1 w-full md:w-auto flex flex-col justify-center gap-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">TOP BY:</span>

                          <div className="relative group flex items-center">
                            <select
                              value={innovationSummaryDimension}
                              onChange={(e) => setInnovationSummaryDimension(e.target.value)}
                              className="appearance-none bg-transparent text-[10px] font-extrabold text-[#eb4a4a] pr-4 py-0 cursor-pointer hover:underline focus:outline-none uppercase tracking-wide"
                            >
                              {innovationDimensionOptions.map((opt) => (
                                <option key={opt.value} value={opt.value} className="text-gray-700 bg-white normal-case">
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <ListFilter className="w-3 h-3 text-[#eb4a4a] absolute right-0 pointer-events-none" />
                          </div>

                          {innovationChipFilterValue && (
                            <button
                              type="button"
                              onClick={() => setInnovationChipFilterValue(null)}
                              className="text-[10px] font-bold text-gray-400 hover:text-[#eb4a4a] truncate"
                              title="Clear filter"
                            >
                              Clear
                            </button>
                          )}
                        </div>

                        {innovationChipSearchEnabled ? (
                          <div className="flex items-center gap-2 w-full min-w-0">
                            <div className="flex-1 min-w-0 opacity-80 hover:opacity-100 transition-opacity">
                              <SearchableSelect
                                options={innovationChipSearchOptions}
                                value={null}
                                onChange={(value) => setInnovationChipFilterValue(value)}
                                placeholder={`Select ${innovationSummaryStats.dimensionLabel}...`}
                                searchPlaceholder="Type to search..."
                                mode="combobox"
                                dropdownClassName="min-w-[20rem]"
                                className="shadow-sm w-full text-sm"
                              />
                            </div>
                            {innovationChipFilterValue && (
                              <button
                                type="button"
                                onClick={() => setInnovationChipFilterValue(null)}
                                className="flex-none flex items-center gap-2 px-2 py-1 rounded border border-[#eb4a4a] bg-[#eb4a4a] text-white whitespace-nowrap"
                                title="Clear filter"
                              >
                                <span className="text-[10px] font-medium">
                                  {innovationChipFilterValue === 'Unspecified' ? 'Empty' : innovationChipFilterValue}
                                </span>
                                <span className="text-[10px] font-bold">×</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-row gap-2 w-full overflow-x-auto pb-1 scrollbar-hide">
                            {innovationChipItemsToRender.map(([name, count], idx) => {
                              const isSelected = innovationChipFilterValue === name;
                              return (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() =>
                                    setInnovationChipFilterValue((current) => (current === name ? null : name))
                                  }
                                  className={`flex-none flex items-center justify-between px-2 py-1 rounded border whitespace-nowrap ${
                                    isSelected
                                      ? 'bg-[#eb4a4a] border-[#eb4a4a] text-white'
                                      : 'bg-gray-50 border-gray-100'
                                  }`}
                                  title={name === 'Unspecified' ? 'Empty' : name}
                                >
                                  <span className={`text-[10px] font-medium mr-2 ${isSelected ? 'text-white' : 'text-gray-700'}`}>
                                    {name === 'Unspecified' ? 'Empty' : name}
                                  </span>
                                  <span className={`text-[10px] font-bold ${isSelected ? 'text-white' : 'text-[#eb4a4a]'}`}>
                                    {count}
                                  </span>
                                </button>
                              );
                            })}

                            {innovationSummaryStats.topItems.length === 0 && (
                              <div className="w-full text-center text-[10px] text-gray-400 italic">No data available</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 gap-1">
                      <BarChart3 className="w-5 h-5 opacity-20" />
                      <p className="text-xs font-medium">Select filters to see innovation summary</p>
                    </div>
                  )
                ) : summaryStats ? (
                  <ActionsSummaryCard
                    stats={summaryStats}
                    dimension={summaryDimension}
                    onDimensionChange={setSummaryDimension}
                    dimensionOptions={dimensionOptions}
                  />
                ) : (
                  <div className="h-full w-full flex flex-col items-center justify-center text-gray-400 gap-1">
                    <BarChart3 className="w-5 h-5 opacity-20" />
                    <p className="text-xs font-medium">Select Country & Client to see summary</p>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden bg-white">
        {(gridSection === 'mitigation' ? mitigationLoading : loading) ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Loader2 className="w-10 h-10 text-[#eb4a4a] animate-spin mx-auto mb-4" />
              <p className="text-[#454240] font-medium">Loading actions...</p>
            </div>
          </div>
        ) : gridSection === 'commercial' ? (
          canSeeCommercialActions && hasValidCommercialSelection && dropdownConfig ? (
            <ActionGrid
              data={actions}
              countryId={selectedCountryValue}
              countryIsoCode={countries[selectedCountryValue]?.isoCode || selectedCountryValue}
              clientId={selectedClientValue}
              dropdownConfig={dropdownConfig}
              onDataChange={handleDataChange}
              onUnsavedChangesChange={setHasUnsavedChanges}
              onClientCreated={handleClientCreated}
              showDeleted={showDeleted}
              showInnovationTeam={canSeeInnovationTeam}
              onSectionChange={(section) => {
                if (section === 'commercial' && !canSeeCommercialActions) return;
                if (section === 'innovation' && !canSeeInnovationTeam) return;
                if (section === 'mitigation' && !canSeeMitigation) return;
                setGridSection(section);
                if (section !== 'innovation') {
                  setSelectedInnovationProjectId(null);
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50/50">
              <div className="text-center text-gray-400">
                <p className="text-lg mb-2 font-medium">Select a country and client to view actions</p>
                <p className="text-sm">
                  {canSeeInnovationTeam
                    ? `Use ${NO_COUNTRY_LABEL} and ${NO_CLIENT_LABEL} only in Innovation Team.`
                    : 'Use the filters above to get started'}
                </p>
              </div>
            </div>
          )
        ) : gridSection === 'mitigation' ? (
          canSeeMitigation && hasValidMitigationSelection && dropdownConfig ? (
            <ActionGrid
              data={mitigationActions}
              countryId={selectedCountryValue}
              countryIsoCode={countries[selectedCountryValue]?.isoCode || selectedCountryValue}
              clientId={selectedClientValue}
              dropdownConfig={dropdownConfig}
              onDataChange={handleMitigationDataChange}
              onUnsavedChangesChange={setHasUnsavedChanges}
              onClientCreated={handleClientCreated}
              showDeleted={showDeleted}
              showInnovationTeam={canSeeInnovationTeam}
              collectionRoot="mitigation"
              onSectionChange={(section) => {
                if (section === 'commercial' && !canSeeCommercialActions) return;
                if (section === 'innovation' && !canSeeInnovationTeam) return;
                if (section === 'mitigation' && !canSeeMitigation) return;
                setGridSection(section);
                if (section !== 'innovation') {
                  setSelectedInnovationProjectId(null);
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50/50">
              <div className="text-center text-gray-400">
                <p className="text-lg mb-2 font-medium">Select a country and client to view actions</p>
                <p className="text-sm">Use the filters above to get started</p>
              </div>
            </div>
          )
        ) : gridSection === 'innovation' && canRenderInnovationGrid ? (
            <ActionGridInnovation
              innovationData={filteredInnovationProjectsForGrid}
              onAddInnovationProject={handleAddInnovationProject}
              selectedInnovationProject={selectedInnovationProject}
              onSelectInnovationProject={handleSelectInnovationProject}
              onUpdateInnovationProject={handleUpdateInnovationProject}
              onDeleteInnovationProject={handleDeleteInnovationProject}
              onAddInnovationUpdate={handleAddInnovationUpdate}
              onCloseInnovationDetails={handleCloseInnovationDetails}
              onSectionChange={(section) => {
                if (section === 'commercial' && !canSeeCommercialActions) return;
                if (section === 'innovation' && !canSeeInnovationTeam) return;
                if (section === 'mitigation' && !canSeeMitigation) return;
                setGridSection(section);
                if (section !== 'innovation') {
                  setSelectedInnovationProjectId(null);
                }
              }}
              clients={clients}
              selectedClientId={selectedClientValue}
              countryName={selectedCountryName}
              leadTechnicianEmail={user?.email || ''}
              showCommercialActionsButton={canSeeCommercialActions && !isInnovationInternalSelection}
              showMitigationButton={canSeeMitigation && !isInnovationInternalSelection}
            />
        ) : (
          <div className="flex items-center justify-center h-full bg-gray-50/50">
            <div className="text-center text-gray-400">
              <p className="text-lg mb-2 font-medium">Select a country and client to view actions</p>
              <p className="text-sm">Use the filters above to get started</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
